/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: api/ringGroupsApi.ts (Ring Groups)
 🗓️ Created: 2025-01-15
 🧩 Description:
    API service layer for Ring Groups.
    Centralizes all API calls for better maintainability.

────────────────────────────────────────────────────────────────*/

import { buildApiUrl, getApiHeaders } from '../../../config/api';
import {
  ListRingGroupsParams,
  ListRingGroupsResponse,
  RingGroupResponse,
  QuickListItem
} from '../types';

/**
 * API endpoint
 */
export const RING_GROUPS_ENDPOINT = '/api/telephony/ring-groups';

/**
 * Normalize headers for fetch
 */
function normalizeHeaders(headers?: HeadersInit): Record<string, string> {
  if (!headers) {
    return {};
  }

  if (headers instanceof Headers) {
    return Object.fromEntries(headers.entries());
  }

  if (Array.isArray(headers)) {
    return headers.reduce<Record<string, string>>((accumulator, [key, value]) => {
      accumulator[key] = value;
      return accumulator;
    }, {});
  }

  return headers;
}

/**
 * Request API with error handling
 */
async function requestApi<T>(
  endpoint: string,
  fallbackMessage: string,
  options: RequestInit = {}
): Promise<{ success: true; data: T; message?: string; pagination?: { page: number; limit: number; total: number; totalPages: number } }> {
  const response = await fetch(
    buildApiUrl(endpoint),
    {
      ...options,
      headers: {
        ...getApiHeaders(),
        ...normalizeHeaders(options.headers),
        ...(options.body && { 'Content-Type': 'application/json' })
      }
    }
  );

  let payload: unknown;

  try {
    payload = await response.json();
  } catch {
    payload = undefined;
  }

  if (!response.ok) {
    const message = payload && typeof payload === 'object' && 'message' in payload
      ? (payload.message as string | undefined)
      : fallbackMessage;
    throw new Error(message || fallbackMessage);
  }

  if (!payload || (typeof payload === 'object' && 'success' in payload && payload.success !== true)) {
    const message = payload && typeof payload === 'object' && 'message' in payload
      ? (payload.message as string | undefined)
      : fallbackMessage;
    throw new Error(message || fallbackMessage);
  }

  if (payload && typeof payload === 'object' && 'success' in payload && payload.success) {
    // At this point payload is a valid API response object; cast to the expected shape.
    return payload as { success: true; data: T; message?: string; pagination?: { page: number; limit: number; total: number; totalPages: number } };
  }

  return {
    success: true,
    data: undefined as T
  };
}

/**
 * Fetch ring groups list
 */
export async function fetchRingGroups(
  params: ListRingGroupsParams
): Promise<ListRingGroupsResponse> {
  const queryParams = new URLSearchParams({
    page: params.page.toString(),
    limit: params.limit.toString(),
    sortBy: params.sortBy,
    sortOrder: params.sortOrder
  });

  if (params.search) {
    queryParams.set('search', params.search);
  }

  if (params.domainId != null) {
    queryParams.set('domainId', params.domainId.toString());
  }

  const result = await requestApi<RingGroupResponse[]>(
    `${RING_GROUPS_ENDPOINT}?${queryParams.toString()}`,
    'Failed to fetch ring groups'
  );

  return {
    success: result.success,
    data: Array.isArray(result.data) ? result.data : [],
    pagination: result.pagination || {
      page: params.page,
      limit: params.limit,
      total: 0,
      totalPages: 0
    }
  };
}

/**
 * Fetch single ring group by ID
 */
export async function fetchRingGroupById(
  id: string | number
): Promise<RingGroupResponse> {
  const result = await requestApi<RingGroupResponse>(
    `${RING_GROUPS_ENDPOINT}/${id}`,
    'Failed to fetch ring group'
  );

  if (!result?.data) {
    throw new Error('No data received from API');
  }

  return result.data;
}

/**
 * Create ring group
 */
export async function createRingGroup(
  payload: {
    name: string;
    extension?: string;
    description?: string;
    context?: string;
    strategy?: string;
    ringTimeout?: number;
    skipBusy?: boolean;
    fallbackModule?: string;
    fallbackDestination?: string;
    enabled?: boolean;
    settings?: unknown;
    members?: unknown;
  }
): Promise<RingGroupResponse> {
  const result = await requestApi<RingGroupResponse>(
    RING_GROUPS_ENDPOINT,
    'Failed to create ring group',
    {
      method: 'POST',
      body: JSON.stringify(payload)
    }
  );
  return result.data;
}

/**
 * Update ring group
 */
export async function updateRingGroup(
  id: string | number,
  payload: {
    name?: string;
    extension?: string;
    description?: string;
    context?: string;
    strategy?: string;
    ringTimeout?: number;
    skipBusy?: boolean;
    fallbackModule?: string;
    fallbackDestination?: string;
    enabled?: boolean;
    settings?: unknown;
    members?: unknown;
  }
): Promise<RingGroupResponse> {
  const result = await requestApi<RingGroupResponse>(
    `${RING_GROUPS_ENDPOINT}/${id}`,
    'Failed to update ring group',
    {
      method: 'PUT',
      body: JSON.stringify(payload)
    }
  );
  return result.data;
}

/**
 * Delete ring group by ID
 */
export async function deleteRingGroupById(
  id: string | number
): Promise<void> {
  await requestApi<null>(
    `${RING_GROUPS_ENDPOINT}/${id}`,
    'Failed to delete ring group',
    {
      method: 'DELETE'
    }
  );
}

/**
 * Fetch ring groups for quick list
 */
export async function fetchQuickListItems(
  limit: number = 100
): Promise<QuickListItem[]> {
  const result = await fetchRingGroups({
    page: 1,
    limit,
    sortBy: 'name',
    sortOrder: 'asc'
  });
  return result.data.map(item => ({
    id: item.id,
    uuid: item.uuid,
    name: item.name
  }));
}

